'use client';

/**
 * Live insets for the "technology" reference screen.
 * OWNED BY THE INSETS STAGE — see ../index.tsx for the contract.
 *
 * Covers (reference pixels, 1672×941):
 *   - depth cards [1077,75,158,80] [1244,75,158,80] [1411,75,178,80] (depth-teacher/method/tech);
 *   - lead sentence [258,160,690,66] and bottom sentence [540,852,600,64];
 *   - the description line of the seven product cards and the two research cards.
 * Titles, icons, the lime frame and the arrows stay as painted. Research prototypes are
 * described by purpose only.
 */

import { useContext, useEffect, useRef, useState, type CSSProperties } from 'react';
import type { ExplanationDepth } from '../../../reference/types';
import { LivingContext } from '../../context';
import { SCENE_CANVAS, rectStyle, type Bilingual, type Rect } from '../../types';
import type { InsetProps } from '../index';
import styles from './technology.module.css';

const CANVAS = SCENE_CANVAS.technology;
const at = (rect: Rect): CSSProperties => rectStyle(rect, CANVAS);
const stagger = (index: number): CSSProperties => ({ '--i': Math.min(index, 5) } as CSSProperties);

const DEPTHS: readonly { id: ExplanationDepth; hotspot: string; rect: Rect; title: Bilingual; sub: Bilingual; icon: 'teacher' | 'method' | 'tech' }[] = [
  { id: 'teacher', hotspot: 'depth-teacher', rect: [1077, 75, 158, 80], title: { ru: 'Учитель', en: 'Teacher' }, sub: { ru: '20 секунд', en: '20 sec' }, icon: 'teacher' },
  /* The painted card is 78 px of text wide: the visible EN title is short, the accessible name keeps the full role. */
  { id: 'methodologist', hotspot: 'depth-method', rect: [1244, 75, 158, 80], title: { ru: 'Методист', en: 'Method' }, sub: { ru: 'Глубже', en: 'Deeper' }, icon: 'method' },
  { id: 'technical', hotspot: 'depth-tech', rect: [1411, 75, 178, 80], title: { ru: 'Тех. эксперт', en: 'Tech expert' }, sub: { ru: 'Глубже', en: 'Deeper' }, icon: 'tech' },
];

const LEAD: Rect = [258, 160, 690, 66];
const BOTTOM: Rect = [540, 852, 600, 64];

/** Description line covers inside the painted cards: rect, background sampled inside the card, alignment. */
const CARD_COVERS: readonly { rect: Rect; bg: string; align: 'center' | 'left' }[] = [
  { rect: [117, 431, 178, 46], bg: '#020c0c', align: 'center' },
  { rect: [326, 430, 202, 46], bg: '#020c0c', align: 'center' },
  { rect: [560, 430, 180, 46], bg: '#010b0b', align: 'center' },
  { rect: [772, 449, 183, 46], bg: '#010a0c', align: 'center' },
  { rect: [180, 604, 200, 44], bg: '#030d0b', align: 'left' },
  { rect: [478, 604, 212, 44], bg: '#030d0b', align: 'left' },
  { rect: [802, 604, 156, 44], bg: '#030e0c', align: 'left' },
];

const RESEARCH_COVERS: readonly Rect[] = [
  [1221, 419, 340, 66],
  [1221, 626, 340, 46],
];

interface DepthCopy {
  lead: Bilingual;
  bottom: Bilingual;
  cards: readonly [Bilingual, Bilingual, Bilingual, Bilingual, Bilingual, Bilingual, Bilingual];
  research: readonly [Bilingual, Bilingual];
}

const COPY: Record<ExplanationDepth, DepthCopy> = {
  teacher: {
    lead: {
      ru: 'Две территории одной системы. Сегодня — надёжный адаптивный опыт.\nИсследуем дальше — новые подходы к пониманию обучения.',
      en: 'Two territories of one system. Today: a dependable adaptive experience.\nNext: new approaches to understanding learning.',
    },
    bottom: {
      ru: 'Мы честно разделяем то, что помогает учиться уже сегодня,\nи то, что требует исследований и времени.',
      en: 'We keep apart what already helps learning today\nand what still needs research and time.',
    },
    cards: [
      { ru: 'Структурирует тему на понятия и связи.', en: 'Structures a topic into concepts and links.' },
      { ru: 'Выявляет, что понятно, а что требует внимания.', en: 'Finds what is clear and what needs attention.' },
      { ru: 'Короткие объяснения под ваш контекст.', en: 'Short explanations for your context.' },
      { ru: 'Закрепление через практику и повторение.', en: 'Consolidation through practice and review.' },
      { ru: 'План обучения, который меняется вместе с вами.', en: 'A learning plan that changes with you.' },
      { ru: 'Помощь в принятии решений и сопровождении ученика.', en: 'Support for decisions and learner guidance.' },
      { ru: 'Диалог, подсказки и поддержка, когда нужно.', en: 'Dialogue, hints and support when needed.' },
    ],
    research: [
      { ru: 'Исследуем, как принципы работы мозга могут помочь лучше моделировать понимание и процесс обучения.', en: 'We explore how principles of brain function may help model understanding and the learning process.' },
      { ru: 'Изучаем универсальные закономерности обучения и способы их формализации в новых моделях.', en: 'We study universal patterns of learning and ways to formalize them in new models.' },
    ],
  },
  methodologist: {
    lead: {
      ru: 'Слева — рабочий цикл: карта знаний, диагностика, микроуроки, маршрут.\nСправа — прототипы новых способов понимать, как человек учится.',
      en: 'Left: the working loop, knowledge map, diagnosis, micro-lessons, route.\nRight: prototypes of new ways to understand how a person learns.',
    },
    bottom: {
      ru: 'Методика опирается только на левую часть.\nПравая часть меняет вопросы, а не уроки.',
      en: 'The method rests on the left side only.\nThe right side changes the questions, not the lessons.',
    },
    cards: [
      { ru: 'Показывает предпосылки и типичные ошибки.', en: 'Shows prerequisites and typical errors.' },
      { ru: 'Ищет причину ошибки, а не только её факт.', en: 'Looks for the cause of an error, not only the fact.' },
      { ru: 'Одна идея, пример, попытка. По силам.', en: 'One idea, an example, an attempt. Within reach.' },
      { ru: 'Проверка в новом контексте, а не разово.', en: 'A check in a new context, not a single try.' },
      { ru: 'Следующий шаг выбирается из состояния и цели.', en: 'The next step is chosen from state and goal.' },
      { ru: 'Причина, основание и варианты: решает учитель.', en: 'Cause, evidence and options: the teacher decides.' },
      { ru: 'Подсказка ведёт к решению, не подменяя его.', en: 'A hint leads to the answer, not past it.' },
    ],
    research: [
      { ru: 'Вопрос исследования: как описать понимание как процесс, а не как балл.', en: 'Research question: how to describe understanding as a process, not a score.' },
      { ru: 'Теоретическая рамка для вопросов, которые продукт пока не задаёт.', en: 'A theoretical frame for questions the product does not ask yet.' },
    ],
  },
  technical: {
    lead: {
      ru: 'Продукт: сигналы, граф знаний и правила маршрута — это работает сегодня.\nПрототипы отделены: они исследуют модели понимания и в продукт не входят.',
      en: 'Product: signals, a knowledge graph and route rules, all running today.\nPrototypes stay separate: models of understanding, not shipped code.',
    },
    bottom: {
      ru: 'Граница проходит по коду: слева — то, что развёрнуто,\nсправа — то, что проверяется как гипотеза.',
      en: 'The boundary runs through the code: deployed on the left,\ntested as a hypothesis on the right.',
    },
    cards: [
      { ru: 'Граф понятий и связей: основа выбора шага.', en: 'A graph of concepts and links: the basis for step choice.' },
      { ru: 'Ответы и подсказки обновляют состояние.', en: 'Answers and hints update the learner state.' },
      { ru: 'Собираются под текущий узел графа.', en: 'Assembled for the current graph node.' },
      { ru: 'Статус узла меняется по подтверждениям.', en: 'Node status changes on confirmed attempts.' },
      { ru: 'Правила маршрута пересчитываются после шага.', en: 'Route rules are recomputed after each step.' },
      { ru: 'Основание каждого предложения видно целиком.', en: 'The evidence behind each suggestion is visible.' },
      { ru: 'Отвечает в контексте сцены и ученика.', en: 'Answers in the context of the scene.' },
    ],
    research: [
      { ru: 'Прототип моделей состояния ученика. В продукт не входит.', en: 'A prototype of learner state models. Not part of the product.' },
      { ru: 'Формальные модели; развёрнутого кода в продукте нет.', en: 'Formal models; no deployed code in the product.' },
    ],
  },
};

function useLiveFlag(key: string): boolean {
  const first = useRef(key);
  const [live, setLive] = useState(false);
  useEffect(() => {
    if (!live && key !== first.current) setLive(true);
  }, [key, live]);
  return live;
}

function DepthIcon({ kind }: { kind: 'teacher' | 'method' | 'tech' }) {
  switch (kind) {
    case 'teacher':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="7.5" r="4" /><path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7" /></svg>;
    case 'method':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="7.5" r="4" /><path d="M4.5 21v-2.5c0-2.8 3.4-4.5 7.5-4.5s7.5 1.7 7.5 4.5V21" /></svg>;
    case 'tech':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.5l8 4.5v10l-8 4.5-8-4.5V7z M12 12l8-4.5M12 12L4 7.5M12 12v9.5" /></svg>;
  }
}

export default function TechnologyInsets({ lang, state, update, spotlight, onInteraction }: InsetProps) {
  const living = useContext(LivingContext);
  const reduced = living?.reducedMotion ?? false;
  const ru = lang === 'ru';
  const depth = state.explanationDepth;
  const live = useLiveFlag(depth);
  const copy = COPY[depth];
  const pick = (value: Bilingual) => (ru ? value.ru : value.en);

  const groupName = ru ? 'Глубина объяснения' : 'Explanation depth';
  const press = (label: string, fn: () => void) => () => {
    onInteraction?.(label);
    fn();
  };

  return (
    <div className={styles.root} data-live={live ? 'true' : undefined} data-reduced={reduced ? 'true' : undefined}>
      <div className={styles.depthGroup} role="radiogroup" aria-label={groupName}>
        {DEPTHS.map((item) => {
          const active = depth === item.id;
          const role = item.id === 'methodologist' && !ru ? 'Methodologist' : pick(item.title);
          const name = `${ru ? 'Глубина' : 'Depth'}: ${role}`;
          return (
            <button
              key={item.id}
              type="button"
              role="radio"
              className={`${styles.depth} ${active ? styles.depthActive : ''}`}
              style={at(item.rect)}
              data-hotspot={item.hotspot}
              data-spotlight={spotlight === item.hotspot ? 'true' : undefined}
              aria-checked={active}
              aria-label={name}
              onClick={press(name, () => update({ explanationDepth: item.id }))}
            >
              <span className={styles.depthIcon}><DepthIcon kind={item.icon} /></span>
              <span className={styles.depthText}>
                <strong>{pick(item.title)}</strong>
                <small>{pick(item.sub)}</small>
              </span>
              {spotlight === item.hotspot && <i className={styles.pulse} aria-hidden="true" />}
            </button>
          );
        })}
      </div>

      <div className={styles.lead} style={at(LEAD)}>
        <p key={depth} className={styles.leadText}>{pick(copy.lead)}</p>
      </div>

      <div className={styles.bottom} style={at(BOTTOM)}>
        <p key={depth} className={styles.bottomText}>{pick(copy.bottom)}</p>
      </div>

      {CARD_COVERS.map((cover, index) => (
        <div
          key={index}
          className={`${styles.card} ${cover.align === 'left' ? styles.cardLeft : ''}`}
          style={{ ...at(cover.rect), background: cover.bg }}
        >
          <p key={depth} style={stagger(index)}>{pick(copy.cards[index])}</p>
        </div>
      ))}

      {RESEARCH_COVERS.map((rect, index) => (
        <div key={index} className={styles.research} style={at(rect)}>
          <p key={depth} style={stagger(index)}>{pick(copy.research[index])}</p>
        </div>
      ))}
    </div>
  );
}
